import { useState, useCallback, useRef, useEffect } from 'react'
import { toast } from 'sonner'
import { apiService } from '@/services/api'
import type { DiceType } from '@/types/dice'
import type { Character } from '@/types'

export interface SharedDiceModalState {
  isOpen: boolean
  sessionId: string
  roller: {
    userId: string
    userName: string
    characterId?: string
    characterName?: string
  }
  diceType: string
  canInteract: boolean
  openTime?: number
  /** Teste pedido pelo mestre (fase 3): o convocado ve o que precisa tirar
   *  ANTES de rolar; os demais veem o que esta sendo testado. */
  teste?: {
    attributeId: string
    attributeName: string
    difficulty?: number
    bonus?: number
    /** Vantagem / desvantagem: a mesa ve antes do dado, como a dificuldade. */
    advantage?: 'vantagem' | 'desvantagem'
    actionName?: string
    requestedByName?: string
  }
  /** Espelho da montagem (fase A do redesenho): o que quem monta ja escolheu,
   *  para a mesa toda ver a MESMA bandeja. So exibicao — o que vale continua
   *  sendo resolvido pelo servidor no /roll. */
  montagem?: {
    dice: DiceType[]
    modifiers: { id: string; name: string; value: number }[]
  }
}

export interface SharedDiceRollingState {
  isActive: boolean
  player: string
  diceType: string
  startTime: number
  userId: string
  characterName?: string
  result?: number
  /** Id da rolagem: quem rolou envia ao chat pela propria cerimonia. */
  rollId?: string
  /** A jogada inteira, para a cerimonia poder DESENHAR a conta e nao so o
   *  total (o problema relatado em uso: "nao da para ver cada dado"). */
  diceSpec?: { type: string; result: number }[]
  kept?: number[]
  rule?: string | null
  expression?: string | null
  modifiers?: { id: string; name: string; value: number }[]
  attributeName?: string
  attributeValue?: number
  bonus?: number
  total?: number
  difficulty?: number
  outcome?: 'success' | 'failure'
  /** Vantagem / desvantagem: `result` e o dado que valeu, `droppedResult` o
   *  outro. A mesa precisa ver os DOIS para a mecanica fazer sentido. */
  advantage?: 'vantagem' | 'desvantagem'
  droppedResult?: number
  /** Critico natural (fase 3): o dado extremo decide sozinho. Vem do
   *  servidor — derivar aqui daria duas verdades sobre a mesma jogada. */
  critical?: 'acerto' | 'erro'
  /** O que estava sendo tentado, nomeado pelo mestre no pedido. */
  actionName?: string
}

interface UseDiceControlsProps {
  tableId: string
  currentUser?: { id: string } | null
  selectedCharacter?: Character | null
}

export function useDiceControls({
  tableId,
  currentUser,
  selectedCharacter
}: UseDiceControlsProps) {
  // Mantém o id do usuário atual acessível dentro dos handlers que o WebSocket
  // congela no momento da conexão (que ocorre antes de o usuário terminar de
  // carregar). Sem o ref, o closure veria currentUser?.id === undefined e o
  // iniciador da rolagem nunca seria reconhecido (canInteract ficaria false).
  const currentUserIdRef = useRef(currentUser?.id)
  useEffect(() => {
    currentUserIdRef.current = currentUser?.id
  }, [currentUser?.id])

  // Estado do modal local
  const [showDiceModal, setShowDiceModal] = useState(false)
  
  // Estado para modal de dados compartilhado
  const [sharedDiceModal, setSharedDiceModal] = useState<SharedDiceModalState>({
    isOpen: false,
    sessionId: '',
    roller: { userId: '', userName: '' },
    diceType: '',
    canInteract: false
  })
  
  // Id da ultima jogada que o mestre mandou repetir (fase B). A bandeja compara
  // com a jogada que tem na tela: so ela volta a montagem.
  const [repetida, setRepetida] = useState<string | null>(null)

  // Estado para sistema de dados compartilhados
  const [sharedDiceRolling, setSharedDiceRolling] = useState<SharedDiceRollingState>({
    isActive: false,
    player: '',
    diceType: '',
    startTime: 0,
    userId: '',
    characterName: undefined,
    result: undefined
  })

  // Handler para abrir modal de dados
  const handleRollDice = useCallback(async () => {
    if (!currentUser || !tableId) {
      toast.error('Erro: usuário ou mesa não identificados')
      return
    }

    try {
      // apiService.request centraliza token/erros e ja lanca em non-ok
      // (antes: fetch cru com path /api hardcoded + token do localStorage).
      await apiService.request('/dice/modal/open', {
        method: 'POST',
        body: JSON.stringify({
          table_id: tableId,
          dice_type: 'D20', // Padrão inicial
          character_id: selectedCharacter?.id
        })
      })

      // Modal aberto com sucesso via API
    } catch (error) {
      console.error('[DICE_MODAL] Erro ao abrir modal compartilhado:', error)
      toast.error('Erro ao abrir modal de dados')
      
      // Fallback para modal local
      setShowDiceModal(true)
    }
  }, [currentUser, tableId, selectedCharacter])

  // Handler para fechar modal local
  const handleDiceModalClose = useCallback(() => {
    setShowDiceModal(false)
  }, [])

  /**
   * Retirar o pedido de rolagem: a bandeja sai da tela de TODOS.
   *
   * Nao fecha mais localmente antes de o servidor confirmar (2026-07-27). A
   * bandeja e uma so e aparece para a mesa inteira; desde que so o mestre a
   * retira (decisao 12), fechar por conta propria deixaria quem tentou sem a
   * tela que todos os outros continuam vendo — o pior dos dois mundos. Quem
   * fecha e o `dice_modal_close` que volta pela mesa.
   */
  const handleSharedDiceModalCloseRequest = useCallback(async () => {
    try {
      await apiService.request('/dice/modal/close', {
        method: 'POST',
        body: JSON.stringify({
          session_id: sharedDiceModal.sessionId,
          reason: 'manual_close'
        })
      })
    } catch (error) {
      toast.error('Só o mestre retira o pedido de rolagem')
    }
  }, [sharedDiceModal.sessionId])

  // NAO existe mais "fechar quando a rolagem completa". A tela unica (fase A
  // do redesenho, 2026-07-26) fica aberta do inicio da montagem ate a jogada
  // virar registro no chat — quem a fecha e o `dice_roll` (publicacao), em
  // handleRolagemPublicada. O fechamento de 1,5s que morava aqui era um dos
  // tres assassinos da tela do jogador (junto com o `dice_modal_close` do
  // /roll e o cronometro de 30s da abertura).

  /** Espelho da montagem chegando pela mesa: atualiza a bandeja de todos. */
  const handleTrayEspelhada = useCallback((data: {
    session_id: string
    user_id: string
    dice: string[]
    modifiers: { id: string; name: string; value: number }[]
  }) => {
    setSharedDiceModal(prev => {
      if (!prev.isOpen || prev.sessionId !== data.session_id) return prev
      return {
        ...prev,
        montagem: {
          dice: data.dice as DiceType[],
          modifiers: data.modifiers,
        },
      }
    })
  }, [])

  // Handler para início de rolagem compartilhada (recebe via WebSocket)
  const handleSharedDiceRollStart = useCallback((data: {
    user_id: string
    user_name: string
    dice_type: string
    character_name?: string
    start_time: string
  }) => {
    // Feedback tatil no celular ao iniciar a rolagem (Android vibra;
    // iOS/Safari nao suporta navigator.vibrate e ignora em silencio).
    // 40ms: abaixo de ~30ms muitos aparelhos nem chegam a acionar o motor.
    if ('vibrate' in navigator) navigator.vibrate(40)

    setSharedDiceRolling({
      isActive: true,
      player: data.user_name,
      diceType: data.dice_type,
      startTime: new Date(data.start_time).getTime(),
      userId: data.user_id,
      characterName: data.character_name,
      result: undefined // Reset resultado ao iniciar nova rolagem
    })

    // Failsafe: se o RESULTADO nunca chegar (rede caiu no meio), a tela nao
    // fica girando para sempre. Rolagem que ja resolveu NAO e limpa aqui —
    // ela congela de proposito, ate virar registro no chat.
    setTimeout(() => {
      setSharedDiceRolling(prev => (
        prev.isActive && prev.result === undefined
          ? { ...prev, isActive: false }
          : prev
      ))
    }, 10000)
  }, [])

  // Handler para fim de rolagem compartilhada (recebe via WebSocket)
  const handleSharedDiceRollEnd = useCallback((data: {
    user_id: string
    user_name: string
    dice_type: string
    result: number
    id?: string
    roll_id?: string
    dice_spec?: { type: string; result: number }[]
    kept?: number[]
    rule?: string | null
    expression?: string | null
    modifiers?: { id: string; name: string; value: number }[]
    attribute_name?: string
    attribute_value?: number
    bonus?: number
    total?: number
    difficulty?: number
    outcome?: 'success' | 'failure'
    advantage?: 'vantagem' | 'desvantagem'
    dropped_result?: number
    critical?: 'acerto' | 'erro'
    action_name?: string
  }) => {
    // "Dado bateu na mesa": vibracao firme no resultado. No critico, tres
    // toques — o 20 natural e o momento que a mesa lembra depois da aula, e o
    // celular pode participar dele de graca.
    if ('vibrate' in navigator) {
      navigator.vibrate(data.critical ? [60, 50, 60, 50, 120] : 80)
    }

    // A cerimonia CONGELA no resultado: nao se apaga sozinha depois de N
    // segundos. Ela sai quando a rolagem vira registro no chat (evento
    // `dice_roll`), seja porque quem rolou enviou, seja pelo envio automatico.
    // Foi o relato de uso que levou a isso: "rolagem rapida demais, nao da
    // para ver o resultado de cada dado nem a conta".
    setSharedDiceRolling(prev => {
      if (!prev.isActive || prev.userId !== data.user_id) return prev
      return {
        ...prev,
        result: data.result,
        rollId: data.id ?? data.roll_id,
        diceSpec: data.dice_spec,
        kept: data.kept,
        rule: data.rule,
        expression: data.expression,
        modifiers: data.modifiers,
        attributeName: data.attribute_name,
        attributeValue: data.attribute_value,
        bonus: data.bonus,
        total: data.total,
        difficulty: data.difficulty,
        outcome: data.outcome,
        advantage: data.advantage,
        droppedResult: data.dropped_result,
        critical: data.critical,
        actionName: data.action_name,
      }
    })
  }, [])

  /**
   * O mestre pediu para repetir (fase B): a jogada sai de cena e a montagem
   * volta — com os dados que estavam na bandeja.
   *
   * O modal de NINGUEM fecha aqui, e e o ponto central: quem rolou continua na
   * mesma tela para corrigir o que o mestre apontou. `repetida` guarda o id da
   * jogada, que a bandeja usa para saber que a jogada dela especificamente
   * caiu (uma repeticao de terceiros nao pode limpar a tela de quem monta).
   */
  const handleRolagemRepetida = useCallback((data: {
    roll_id: string
    user_id: string
  }) => {
    setRepetida(data.roll_id)
    setSharedDiceRolling(prev => {
      if (!prev.isActive) return prev
      // A jogada e' identificada pelo id quando ele existe; o userId cobre o
      // caso de a bandeja ainda nao ter recebido o fim da rolagem.
      if (prev.rollId && prev.rollId !== data.roll_id) return prev
      if (!prev.rollId && prev.userId !== data.user_id) return prev
      return { ...prev, isActive: false, result: undefined, rollId: undefined }
    })
  }, [])

  /**
   * A rolagem virou registro no chat: o momento acabou, a tela unica sai.
   *
   * Fecha pela IDENTIDADE de quem rolou, nao por "qualquer publicacao": se o
   * mestre despachar do historico uma rolagem parada de terceiros, a bandeja
   * aberta de outra pessoa nao pode sumir no meio da montagem.
   */
  const handleRolagemPublicada = useCallback((data?: { user_id?: string }) => {
    setSharedDiceRolling(prev => {
      if (!prev.isActive) return prev
      if (data?.user_id && prev.userId && data.user_id !== prev.userId) return prev
      return { ...prev, isActive: false }
    })
    setSharedDiceModal(prev => {
      if (!prev.isOpen) return prev
      if (data?.user_id && prev.roller.userId && data.user_id !== prev.roller.userId) return prev
      return { ...prev, isOpen: false, montagem: undefined }
    })
  }, [])

  // Handler para abrir modal compartilhado (recebe via WebSocket)
  const handleSharedDiceModalOpen = useCallback((data: {
    session_id: string
    user_id: string
    user_name: string
    character_id?: string
    character_name?: string
    dice_type?: string
    open_time: string
    attribute_id?: string
    attribute_name?: string
    difficulty?: number
    bonus?: number
    advantage?: 'vantagem' | 'desvantagem'
    action_name?: string
    requested_by_name?: string
  }) => {
    // Apenas quem iniciou a rolagem escolhe o dado; os demais apenas assistem.
    // Lê do ref para não depender do closure congelado pelo WebSocket.
    const isInitiator = currentUserIdRef.current === data.user_id
    const canInteract = isInitiator

    // Sessao nova = momento novo: residuo de jogada ENCERRADA nao atravessa a
    // fronteira. Uma jogada ainda viva (congelada esperando envio) fica — o
    // envio automatico a resolve; so o cadaver (isActive false, resultado
    // residual) e varrido, para nao reaparecer na bandeja de quem assiste.
    setSharedDiceRolling(prev => (
      prev.isActive ? prev : {
        isActive: false,
        player: '',
        diceType: '',
        startTime: 0,
        userId: '',
        characterName: undefined,
        result: undefined,
      }
    ))

    setSharedDiceModal({
      isOpen: true,
      sessionId: data.session_id,
      roller: {
        userId: data.user_id,
        userName: data.user_name,
        characterId: data.character_id,
        characterName: data.character_name
      },
      diceType: data.dice_type || 'D20',
      canInteract,
      openTime: new Date(data.open_time).getTime(),
      teste: data.attribute_id && data.attribute_name ? {
        attributeId: data.attribute_id,
        attributeName: data.attribute_name,
        difficulty: data.difficulty,
        bonus: data.bonus,
        advantage: data.advantage,
        actionName: data.action_name,
        requestedByName: data.requested_by_name
      } : undefined
    })
    
    console.log(`[SHARED_MODAL] Modal opened - Can interact: ${canInteract}, Is initiator: ${isInitiator}`)

    // Sem cronometro de fechamento. Havia aqui um "auto-fechar em 30s" — na
    // fase A a montagem e um momento coletivo sem pressa: escolher tres dados
    // e dois chips conversando com a mesa leva mais que isso. A tela sai
    // quando a jogada vira registro, ou quando quem monta desiste (fechar
    // manual, que e transmitido para todos).
  }, [])

  // Handler para fechar modal compartilhado (recebe via WebSocket)
  const handleSharedDiceModalClose = useCallback((data: {
    session_id: string
    reason: string
  }) => {
    setSharedDiceModal(prev => ({
      ...prev,
      isOpen: false
    }))
    
    // Limpar completamente o estado após um delay
    setTimeout(() => {
      setSharedDiceModal({
        isOpen: false,
        sessionId: '',
        roller: { userId: '', userName: '' },
        diceType: '',
        canInteract: false
      })
    }, 500)
  }, [])

  return {
    // Estado
    showDiceModal,
    sharedDiceModal,
    sharedDiceRolling,
    repetida,

    // Handlers
    handleRollDice,
    handleDiceModalClose,
    handleSharedDiceModalCloseRequest,
    handleSharedDiceRollStart,
    handleSharedDiceRollEnd,
    handleRolagemPublicada,
    handleRolagemRepetida,
    handleTrayEspelhada,
    handleSharedDiceModalOpen,
    handleSharedDiceModalClose
  }
}